import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import MainCard from 'components/MainCard';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Stack, Typography } from '@mui/material';
import comptroller from 'assets/images/comptroller_white.svg';
import editIcon from 'assets/images/table-icons/edit_green.svg';
import ComptrollerConfirmModal from '../ComptrollerConfirmModal';
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Form, Input, Row, Select, Tooltip } from 'antd';
import { useGetModuleListMutation, useGetVerticalListMutation } from 'store/features/process/processApi';
import { useActivateKmaMutation, useDeactivateKmaMutation } from 'store/features/comptroller/comptrollerApi';

const titleFormat = (code, name) => {
	return `${code} - ${name}`;
};

function DetailsKeyManagementArea() {
	const theme = useTheme();
	const state = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const [subModule, setSubModule] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [submitData, setSubmitData] = useState(null);
	const [submitForm, setSubmitForm] = useState(false);
	const [moduleOptions, setModuleOptions] = useState([]);
	const [kmaListOption, setKmaListOption] = useState([]);
	const [modalData, setModalData] = useState({
		head: 'Solution',
		action: '',
	});
	const [stateValues, setStateValues] = useState({
		kmaDetails: null,
		kmaList: [],
		solutionTable: [],
	});

	const { kmaDetails, solutionTable, kmaList } = stateValues;

	const { profileData } = useSelector((state) => state?.profile);

	const [activateKma, { data: activateKmaRes }] = useActivateKmaMutation();
	const [getModuleList, { data: moduleList }] = useGetModuleListMutation();
	const [getVerticalList, { data: verticalList }] = useGetVerticalListMutation();
	const [deactivateKma, { data: deactivateKmaRes }] = useDeactivateKmaMutation();

	const activateDeactivateHandler = (id, solutionStatus) => {
		const payload = {
			id: id,
			status: solutionStatus,
		};

		setSubmitData(payload);
		setShowModal(true);
		setModalData({
			head: 'Key Management Area (KMA)',
			action: solutionStatus === 'W-I-P' || solutionStatus === 'Inactive' ? 'activate this KMA' : 'deactivate this KMA',
		});
	};

	useEffect(() => {
		getVerticalList({
			companyCode: profileData?.creatorOrganizationId,
		});

		getModuleList({});
	}, []);

	useEffect(() => {
		if (state?.state) {
			setStateValues({
				kmaList: state.state.kmaList,
				kmaDetails: state?.state?.kmaDetails,
				solutionTable: state?.state?.solutionTable,
			});
		} else {
			navigate('/comptroller/key-management-area');
		}
	}, [state?.state]);

	useEffect(() => {
		if (kmaDetails) {
			form.setFieldValue('code', kmaDetails?.code);
			form.setFieldValue('name', kmaDetails?.name);
			form.setFieldValue('moduleId', kmaDetails?.moduleId);
			form.setFieldValue('subModuleId', kmaDetails?.subModuleId);
			form.setFieldValue('parentKAMId', kmaDetails?.parentKAMId ? kmaDetails?.parentKAMId : 'Not Available');
		}
	}, [kmaDetails]);

	useEffect(() => {
		if (moduleList && !moduleOptions?.length) {
			const arr = [];

			for (let i = 0; i < moduleList?.listLynProcessModule?.length; i++) {
				if (moduleList?.listLynProcessModule[i]?.activationStatus === 'Active' && moduleList?.listLynProcessModule[i]?.parent === '') {
					arr.push(moduleList?.listLynProcessModule[i]);
				}
			}

			setModuleOptions(arr);
		}

		if (kmaList) {
			const arr = [];

			for (let i = 0; i < kmaList?.length; i++) {
				if (
					(kmaList[i]?.activationStatus?.toLowerCase() === 'active' || kmaDetails?.parentKAMId === kmaList?.[i]?.id) &&
					kmaDetails?.id !== kmaList[i]?.id
				) {
					arr.push({
						value: kmaList[i]?.id,
						label: kmaList[i]?.name,
					});
				}
			}

			setKmaListOption(arr);
		}

		if (kmaDetails && moduleList) {
			const arr = [];

			for (let i = 0; i < moduleList?.listLynProcessModule?.length; i++) {
				if (
					moduleList?.listLynProcessModule[i]?.activationStatus === 'Active' &&
					moduleList?.listLynProcessModule[i]?.parent === kmaDetails?.moduleId
				) {
					arr.push(moduleList?.listLynProcessModule[i]);
				}
			}
			setSubModule(arr);
		}
	}, [moduleList, verticalList, kmaList, kmaDetails]);

	useEffect(() => {
		if (activateKmaRes) {
			if (activateKmaRes?.status === 'SUCCESS') {
				toast.success('Success! Action completed.', toastConfig);

				navigate('/comptroller/key-management-area');
			} else if (activateKmaRes?.status === 'FAILED') {
				toast.error('Failure! Action not completed.', toastConfig);
			}
		} else if (deactivateKmaRes) {
			if (deactivateKmaRes?.status === 'SUCCESS') {
				toast.success('Success! Action completed.', toastConfig);

				navigate('/comptroller/key-management-area');
			} else if (deactivateKmaRes?.status === 'FAILED') {
				toast.error('Failure! Action not completed.', toastConfig);
			}
		}
	}, [activateKmaRes, deactivateKmaRes]);

	useEffect(() => {
		if (submitData?.status === 'Inactive' || submitData?.status === 'W-I-P') {
			activateKma({ id: submitData?.id });

			setSubmitData(null);
		} else if (submitData?.status === 'Active') {
			deactivateKma({ id: submitData?.id });

			setSubmitData(null);
		}
	}, [submitForm]);

	return (
		<>
			<Grid container rowSpacing={4.5} columnSpacing={2.75}>
				<Grid item xs={24} lg={24}>
					<Flex justify="flex-end" align="flex-end">
						<Button
							type="text"
							size="large"
							icon={<CaretLeftOutlined style={{ color: theme.palette.lynTheme.arrow }} />}
							onClick={() => {
								navigate(-1);
							}}
						></Button>

						<Button
							type="text"
							size="large"
							icon={<CaretRightOutlined style={{ color: theme.palette.lynTheme.arrow }} />}
							onClick={() => {
								navigate(1);
							}}
						></Button>
					</Flex>

					<Box sx={{ bgcolor: '#214689', borderRadius: 1, pl: '10px', pr: '8px', py: '8px', color: 'common.white' }}>
						<Stack direction="row" alignItems="center" sx={{ gap: '10px' }}>
							<img alt="comptroller" src={comptroller} width="18px" height="18px" />
							<Typography variant="h4">View Key Management Area</Typography>
						</Stack>
					</Box>
				</Grid>
			</Grid>

			<div>
				<MainCard sx={{ mt: 2 }}>
					<Form form={form} id="coe-module" layout="vertical" requiredMark={false} autoComplete="off">
						<Row gutter={[16, 0]}>
							<Col xs={24} sm={24} md={24} xl={24}>
								<Form.Item
									className="animate__animated animate__fadeIn"
									label="Module Name"
									name="moduleId"
									rules={[
										{
											required: true,
											message: 'Module is required',
										},
									]}
								>
									<Select
										allowClear
										showSearch
										style={{ width: '100%' }}
										filterOption={true}
										placeholder="Select from the list"
										optionFilterProp="children"
										autoComplete="off"
										disabled={true}
									>
										{moduleOptions?.map(({ id, name, code }) => (
											<Select.Option key={id} value={code}>
												{titleFormat(code, name)}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</Col>

							<Col xs={24} sm={24} md={24} xl={24}>
								<Form.Item
									name="subModuleId"
									label="Sub Module Name"
									className="animate__animated animate__fadeIn"
									rules={[
										{
											required: true,
											message: 'Sub Module is required',
										},
									]}
								>
									<Select
										allowClear
										showSearch
										style={{ width: '100%' }}
										filterOption={true}
										placeholder="Select from the list"
										optionFilterProp="children"
										autoComplete="off"
										disabled={true}
									>
										{subModule?.map(({ id, name, code }) => (
											<Select.Option key={id} value={code}>
												{titleFormat(code, name)}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</Col>

							<Col xs={24} sm={24} md={24} xl={24}>
								<Form.Item className="animate__animated animate__fadeIn" label="KMA Parent Name" name="parentKAMId" rules={[]}>
									<Select
										allowClear
										disabled={true}
										showSearch
										style={{ width: '100%' }}
										filterOption={true}
										placeholder="Select from the list"
										optionFilterProp="children"
										autoComplete="off"
									>
										{kmaListOption?.map(({ value, label }) => (
											<Select.Option key={value} value={value}>
												{label}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</Col>

							<Col xs={24} sm={24} md={24} xl={24}>
								<Form.Item
									name="name"
									label="KMA Name"
									className=" animate__animated animate__fadeIn"
									rules={[
										{
											required: true,
										},
									]}
								>
									<Input disabled placeholder="" />
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} xl={24}>
								<Form.Item
									name="code"
									label="KMA Code"
									className=" animate__animated animate__fadeIn"
									rules={[
										{
											required: true,
										},
									]}
								>
									<Input disabled placeholder="" />
								</Form.Item>
							</Col>
						</Row>
					</Form>

					<Flex style={{ marginTop: '10px' }} justify="flex-end" align="flex-end">
						<Tooltip title="Edit">
							<Button
								style={{ margin: '1px' }}
								icon={<img src={editIcon} width={16} height={16} alt="edit" />}
								onClick={(e) => {
									e.stopPropagation();

									navigate('/comptroller/key-management-area/coe-kma', {
										state: { kmaDetails: kmaDetails, solutionTable, kmaList },
									});
								}}
							>
								Edit
							</Button>
						</Tooltip>

						<Tooltip title={kmaDetails?.activationStatus === 'Active' ? 'Deactivate' : 'Activate'}>
							<Button
								type="primary"
								style={{
									margin: '3px',
									backgroundColor:
										kmaDetails?.activationStatus === 'Active' ? theme.palette.lynTheme.delete : theme.palette.lynTheme.success,
								}}
								onClick={(e) => {
									e.stopPropagation();

									activateDeactivateHandler(kmaDetails?.id, kmaDetails?.activationStatus);
								}}
							>
								{kmaDetails?.activationStatus === 'Active' ? 'Deactivate' : 'Activate'}
							</Button>
						</Tooltip>
					</Flex>
				</MainCard>

				{showModal && (
					<ComptrollerConfirmModal showModal={showModal} setShowModal={setShowModal} setSubmitForm={setSubmitForm} modalData={modalData} />
				)}
			</div>
		</>
	);
}

export default DetailsKeyManagementArea;
